/**
 * block-actions.js — Delegación de eventos por CLASE para la interacción de los bloques.
 *
 * Por qué existe (Fase 2b):
 *   El saneo XSS server-side (core_services/html_sanitizer.py) borra TODOS los atributos
 *   on* del HTML publicado. Los renderers del builder horneaban on* inline LEGÍTIMOS
 *   (menú móvil, play/pausa de vídeo, cerrar CTA, interacción del mapa), así que el saneo
 *   los mataba. Solución: mover esa lógica aquí, enganchada por CLASE (que sobrevive al
 *   saneo) con UN listener delegado en `document`. Consecuencia buscada: re-sanear un sitio
 *   ya publicado (quita los on*) NO lo rompe — esta delegación lo REVIVE por clase, sin
 *   re-publicar.
 *
 *   Imita el patrón de public-init.js (router público) y blocks/menu.js (installMenuEditHandlers):
 *   un único addEventListener en document + e.target.closest('.clase').
 *
 *   Los handlers son ESTÁTICOS (no interpolan datos de usuario) → no hay nada que escapar.
 *   Debe correr en el sitio PÚBLICO (no se gatea a editorMode) y también en el preview del editor.
 *
 *   Excepción por TAMAÑO, no por criterio: el asistente del bloque `reserva-live` es una máquina
 *   de estados de varios cientos de líneas (calendario, huecos, confirmación, degradación). Vive
 *   en core/reserva-live-runtime.js pero se ARRANCA desde aquí, así que este módulo sigue siendo
 *   el ÚNICO punto de entrada de la interacción de bloques (y los dos ENTRY —script.js y
 *   public-init.js— no necesitan enterarse de nada nuevo).
 */
import { initReservaLive, hydrateReservaLive } from './reserva-live-runtime.js';
// La carta del ERP en vivo (T1b): mismo arranque que la reserva (al cargar y tras cada render).
import { hydrateCartaLive } from './carta-live-runtime.js';
import { initTienda, hydrateTienda } from './tienda-runtime.js';
import { initEntradas, hydrateEntradas } from './entradas-runtime.js';

// ─────────────────────────────────────────────────────────────────
//  BARRA DE ANUNCIO SUPERIOR (cta-bar v3) + CIERRE PERSISTIDO
//  El cta-bar vive DENTRO de #resultado, que tiene container-type:inline-size y
//  ATRAPA position:fixed (por eso el header se "iza" fuera). Para que la barra de
//  anuncio v3 quede fija de verdad ARRIBA del viewport (sin desplazarse con el
//  scroll ni solaparse con el header hoisteado), la CLONAMOS a <body> (escapa del
//  contenedor) y bajamos el header + el contenido con un CSS var. En el editor se
//  re-sincroniza en cada render vía un MutationObserver; en el sitio publicado
//  (DOM estático) basta la pasada inicial. El cierre se PERSISTE en localStorage
//  (clave por HASH del texto → si el dueño cambia el mensaje, se vuelve a mostrar).
// ─────────────────────────────────────────────────────────────────

// Hash djb2 → clave de localStorage estable y corta a partir del texto del anuncio.
function _djb2(s) { let h = 5381; for (let i = 0; i < s.length; i++) h = (((h << 5) + h) + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); }
function _ctaText(bar) { const t = bar.querySelector('.cta-bar-text'); return (t ? t.textContent : '').trim(); }
function _ctaKey(bar) { return 'n3i-cta-dismiss:' + _djb2(_ctaText(bar) || 'default'); }
function _ctaDismissed(bar) { try { return localStorage.getItem(_ctaKey(bar)) === '1'; } catch (_) { return false; } }
function _ctaDismiss(bar) { try { localStorage.setItem(_ctaKey(bar), '1'); } catch (_) { /* private mode */ } }

// El canvas de edición marca <body class="editor-active"> (ahí la barra está oculta).
function _isEditingCanvas() { return typeof document !== 'undefined' && document.body.classList.contains('editor-active'); }
// El sitio REAL publicado NO carga el chrome del editor (no existe #toggle-editor-mode) →
// solo ahí persistimos/saltamos el cierre; en el editor la barra siempre se muestra.
function _isLiveSite() { return typeof document !== 'undefined' && !document.getElementById('toggle-editor-mode'); }

// Retira la barra izada + el empuje del header/contenido (estado limpio).
function _teardownAnnounce() {
    document.querySelectorAll('body > .cta-sticky-bar[data-n3i-cta-hoisted]').forEach((el) => el.remove());
    // Restaurar el `display` de los originales que syncAnnounceBars ocultó al izar su clon. Sin
    // esto, una 2ª pasada sobre un DOM no re-renderizado no re-encontraría la barra (su `find`
    // descarta las display:none) → barra desaparecida. syncAnnounceBars vuelve a aplicar el
    // descarte (localStorage) justo después, así que restaurar aquí es un reset limpio.
    document.querySelectorAll('#resultado .cta-sticky-bar.variant-v3').forEach((el) => {
        if (el.style.display === 'none') el.style.removeProperty('display');
    });
    document.body.classList.remove('n3i-has-announce');
    document.body.style.removeProperty('--n3i-announce-h');
}

/**
 * Sincroniza las barras cta: oculta las descartadas (persistencia) e iza la de
 * anuncio superior (v3) al viewport bajando el header. Idempotente y defensivo.
 */
function syncAnnounceBars() {
    if (typeof document === 'undefined') return;
    _teardownAnnounce();
    if (_isEditingCanvas()) return;               // en el canvas la barra está oculta

    const live = _isLiveSite();
    const bars = Array.from(document.querySelectorAll('.cta-sticky-bar'));

    // 1) Persistencia: en el sitio real, ocultar las barras que el visitante ya cerró.
    if (live) bars.forEach((b) => { if (_ctaDismissed(b)) b.style.display = 'none'; });

    // 2) Barra de anuncio SUPERIOR (v3): izarla a <body> (fija de verdad) y bajar el header.
    const top = bars.find((b) => b.classList.contains('variant-v3')
        && b.style.display !== 'none' && !(live && _ctaDismissed(b)));
    if (!top) return;

    const clone = top.cloneNode(true);
    clone.setAttribute('data-n3i-cta-hoisted', '1');
    clone.style.display = '';
    top.style.display = 'none';                    // el clone es el visible → sin duplicado
    document.body.appendChild(clone);

    const h = clone.offsetHeight || 46;
    document.body.style.setProperty('--n3i-announce-h', h + 'px');
    document.body.classList.add('n3i-has-announce');
}

// ─────────────────────────────────────────────────────────────────
//  ACORDEÓN DEL FAQ (variante v4 "Acordeón glass")
//  block-meta etiqueta esa variante como acordeón, así que tiene que plegar de verdad.
//  El render (blocks/faq.js) marca la pregunta con .faq-q (role=button + aria-expanded +
//  aria-controls) y envuelve la respuesta en .faq-a; aquí va TODO el comportamiento, por
//  delegación de clase — un `onclick` inline no sobreviviría al saneo XSS del publicado.
//  En el canvas de edición NO se pliega nada (el CSS lo deja abierto): plegar mientras el
//  cliente escribe en el contenteditable de la pregunta sería insufrible.
// ─────────────────────────────────────────────────────────────────
function _toggleFaq(pregunta) {
    if (_isEditingCanvas()) return;
    const card = pregunta.closest('.faq-card');
    if (!card) return;
    const abierta = card.classList.toggle('is-open');
    pregunta.setAttribute('aria-expanded', abierta ? 'true' : 'false');
}

// ─────────────────────────────────────────────────────────────────
//  CARTA DEL ERP: PESTAÑAS y «VER TODOS» («Personalizar la carta», `utils/carta.js`)
//  Las pinta `htmlCarta` dentro de `.menu-categories`, también al repintar en vivo: por eso van por
//  delegación (el elemento cambia, la clase no). Pestaña ↔ panel por posición (`data-menu-tab` ↔
//  `data-menu-panel`). Funcionan también en el editor: el dueño revisa cada sección sin publicar.
// ─────────────────────────────────────────────────────────────────
function _activarPestana(tab) {
    const caja = tab.closest('.menu-categories');
    if (!caja) return;
    const k = tab.getAttribute('data-menu-tab');
    caja.querySelectorAll('.menu-tab').forEach((t) => {
        const activa = t === tab;
        t.classList.toggle('is-active', activa);
        t.setAttribute('aria-selected', activa ? 'true' : 'false');
    });
    caja.querySelectorAll('.menu-category[data-menu-panel]').forEach((p) => {
        const activo = p.getAttribute('data-menu-panel') === k;
        p.classList.toggle('is-active', activo);
        // Lo que estaba oculto no llegó a recibir su `.in-view` de entrada: visible ya.
        if (activo) p.querySelectorAll('.stagger-item').forEach((it) => it.classList.add('in-view'));
    });
}

function _verTodos(btn) {
    const cat = btn.closest('.menu-category');
    if (!cat) return;
    cat.classList.add('menu-abierta');
    cat.querySelectorAll('.menu-dish-mas').forEach((it) => it.classList.add('in-view'));
    btn.setAttribute('aria-expanded', 'true');
    btn.hidden = true;
}

/**
 * Instala la delegación de eventos de los bloques. Idempotente: seguro llamarlo varias veces
 * (público + editor comparten guard). NO gatea a editorMode: corre también en el sitio público.
 */
export function initBlockActions() {
    if (typeof window === 'undefined' || window.__n3iBlockActions) return;
    window.__n3iBlockActions = true;

    // ── CLICK: handlers funcionales ────────────────────────────────
    document.addEventListener('click', (e) => {
        const target = e.target;

        // Menú móvil (header.js .menu-toggle): abre/cierra el nav dropdown.
        const menuBtn = target.closest('.menu-toggle');
        if (menuBtn) {
            const nav = menuBtn.parentElement
                && menuBtn.parentElement.querySelector('.nav-links-container');
            if (nav) nav.classList.toggle('open');
            return;
        }

        // Play/pausa del vídeo nativo MP4 (video.js .video-play-btn).
        const playBtn = target.closest('.video-play-btn');
        if (playBtn) {
            const section = playBtn.closest('section');
            const v = section && section.querySelector('video');
            if (!v) return;
            const playIcon = playBtn.querySelector('.play-icon');
            const pauseIcon = playBtn.querySelector('.pause-icon');
            if (v.paused) {
                v.play();
                if (playIcon) playIcon.style.display = 'none';
                if (pauseIcon) pauseIcon.style.display = 'block';
            } else {
                v.pause();
                if (playIcon) playIcon.style.display = 'block';
                if (pauseIcon) pauseIcon.style.display = 'none';
            }
            return;
        }

        // Cerrar la CTA Bar fija (cta-bar.js .cta-bar-close).
        const closeBtn = target.closest('.cta-bar-close');
        if (closeBtn) {
            const bar = closeBtn.closest('.cta-sticky-bar');
            if (bar) {
                bar.style.display = 'none';
                // Persistir el cierre SOLO en el sitio real (no en el editor, para que el
                // dueño siga viendo su barra al editar). Clave por hash del texto.
                if (_isLiveSite()) _ctaDismiss(bar);
                // Si era la barra de anuncio superior (izada), retirar el empuje del header.
                if (bar.hasAttribute('data-n3i-cta-hoisted') || bar.classList.contains('variant-v3')) {
                    _teardownAnnounce();
                }
            }
            return;
        }

        // Carta del ERP: cambiar de pestaña / desplegar el resto de una sección.
        const menuTab = target.closest('.menu-tab');
        if (menuTab) {
            _activarPestana(menuTab);
            return;
        }
        const verTodos = target.closest('.menu-ver-todos');
        if (verTodos) {
            _verTodos(verTodos);
            return;
        }

        // Pregunta del FAQ acordeón (faq.js v4 .faq-q): pliega/despliega su respuesta.
        const faqQ = target.closest('.faq-q');
        if (faqQ) {
            _toggleFaq(faqQ);
            return;
        }

        // Overlay del mapa (maps.js .map-overlay): "clic para interactuar" →
        // oculta el overlay y habilita los eventos de puntero del <object> hermano.
        const overlay = target.closest('.map-overlay');
        if (overlay) {
            overlay.style.opacity = '0';
            if (overlay.nextElementSibling) {
                overlay.nextElementSibling.style.pointerEvents = 'auto';
            }
            return;
        }
    });

    // ── KEYDOWN: el acordeón del FAQ también con teclado ───────────
    // .faq-q es role="button" con tabindex="0": Enter y Espacio deben activarlo (WAI-ARIA).
    // Se ignora si el foco está DENTRO de un contenteditable (en el editor, escribir un
    // espacio en la pregunta no puede plegar la tarjeta).
    document.addEventListener('keydown', (e) => {
        // Pestañas de la carta: ← / → pasan a la vecina (patrón WAI-ARIA de pestañas).
        if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && e.target && e.target.closest) {
            const tab = e.target.closest('.menu-tab');
            const fila = tab && tab.parentElement;
            if (fila) {
                const tabs = Array.prototype.slice.call(fila.querySelectorAll('.menu-tab'));
                const i = tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1);
                const otra = tabs[(i + tabs.length) % tabs.length];
                if (otra) {
                    e.preventDefault();
                    otra.focus();
                    _activarPestana(otra);
                }
                return;
            }
        }
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        if (!e.target || !e.target.closest || e.target.isContentEditable) return;
        const pregunta = e.target.closest('.faq-q');
        if (!pregunta) return;
        e.preventDefault();
        _toggleFaq(pregunta);
    });

    // ── MOUSEOUT: re-bloqueo del mapa al salir con el ratón ────────
    // Reemplaza el onmouseleave del .maps-wrapper (mouseleave no burbujea; usamos
    // mouseout delegado y comprobamos relatedTarget para detectar la salida real).
    document.addEventListener('mouseout', (e) => {
        const wrapper = e.target.closest('.maps-wrapper');
        if (!wrapper) return;
        // Si el ratón sigue dentro del wrapper (movimiento entre hijos), ignorar.
        if (e.relatedTarget && wrapper.contains(e.relatedTarget)) return;
        const overlay = wrapper.querySelector('.map-overlay');
        const obj = wrapper.querySelector('object');
        if (overlay) overlay.style.opacity = '1';
        if (obj) obj.style.pointerEvents = 'none';
    });

    // ── ASISTENTE DE RESERVA EN VIVO ───────────────────────────────────────────
    // Instala su propia delegación (`.rl-*`) y pinta los widgets ya presentes.
    initReservaLive();
    // ── CARTA DEL ERP EN VIVO (bloque `menu` en modo ERP) ─────────────────────
    // Solo en el publicado y en la SPA pública (el runtime lo decide con `basePublica()`).
    hydrateCartaLive();

    // ── SECCIONES DE TIENDA (ofertas, novedades, más vendidos, sueles comprar) ─────────────
    // Piden sus datos vivos y pintan las tarjetas; las flechas del carrusel van por delegación.
    initTienda();

    // ── VENTA DE ENTRADAS (bloque `entradas` y tag `data-n3i-entradas` de las páginas a mano) ──
    initEntradas();

    // ── BARRA DE ANUNCIO SUPERIOR: pasada inicial + re-sync en cada render ──────
    // Público (DOM estático): basta la pasada inicial. Editor (re-renderiza #resultado):
    // el observer re-iza la barra tras cada renderPage. Observa SOLO los hijos directos
    // (childList, sin subtree) → salta en re-renders completos, no al teclear; y como el
    // clon va a <body> y el original se oculta por estilo, no se auto-dispara (sin bucle).
    syncAnnounceBars();
    const root = document.getElementById('resultado');
    if (root && typeof MutationObserver !== 'undefined') {
        let raf = 0;
        const obs = new MutationObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                syncAnnounceBars();
                // Mismo observer, mismo motivo: tras un renderPage los widgets de reserva son
                // elementos NUEVOS (sin pintar). Reusar este observer evita registrar otro y
                // evita que el runtime tenga que importar core/render.js (gotcha #3: los hooks
                // de post-render solo pueden colgar de un ENTRY).
                hydrateReservaLive();
                hydrateCartaLive();
                // Igual con las secciones de tienda: tras un renderPage son elementos nuevos.
                hydrateTienda();
                // Y con el widget de entradas (en una página a mano no hay #resultado: le basta
                // la pasada de initEntradas).
                hydrateEntradas();
            });
        });
        obs.observe(root, { childList: true });
    }
}
